import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import UserDetails from '../../TimeSheetManagement/components/UserDetails';
import './DeploymentReport.css';
import {
  Briefcase,
  Users,
  Calendar,
  Building2,
  Clock,
  RotateCw,
  Download,
  Printer,
  Search,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  X,
  Filter
} from 'lucide-react';

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarBg = (name) => {
  const colors = [
    'linear-gradient(135deg, #3b5bfd, #2563eb)',
    'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    'linear-gradient(135deg, #60a5fa, #1d4ed8)',
    'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    'linear-gradient(135deg, #f59e0b, #b45309)',
    'linear-gradient(135deg, #ec4899, #be185d)',
    'linear-gradient(135deg, #06b6d4, #0e7490)',
    'linear-gradient(135deg, #6366f1, #4338ca)'
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatRate = (rate, type) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const num = parseFloat(rate);
  if (isNaN(num)) return '—';
  const suffix = type ? ` /${type.toLowerCase()}` : '/hr';
  return `$${num.toFixed(2)}${suffix}`;
};

const calculateDurationBadge = (startDate, endDate, status) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now = new Date();
  
  if (endDate) {
    const end = new Date(endDate);
    const diffDays = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <span className="dr-duration-badge ended">Ended</span>;
    }
    if (diffDays <= 30) {
      return (
        <span className="dr-duration-badge ending-soon">
          <Clock size={12} /> {diffDays === 0 ? 'Ends Today' : `Ends in ${diffDays}d`}
        </span>
      );
    }
  }

  // Calculate ongoing active duration
  const months = Math.max(1, Math.round((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)));
  return (
    <span className="dr-duration-badge active">
      Active · {months} {months === 1 ? 'mo' : 'mos'}
    </span>
  );
};

const SESSION_STORAGE_KEY = 'hrm_deployment_report_filters';

const getStoredFilters = () => {
  try {
    const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Could not read sessionStorage for deployment report:', e);
  }
  return {
    statusFilter: 'ACTIVE',
    clientFilter: 'ALL',
    datePreset: 'ALL_TIME',
    startDate: '',
    endDate: '',
    search: '',
    currentPage: 1,
    pageSize: 10
  };
};

const DeploymentReport = () => {
  const initialFilters = getStoredFilters();

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    activeCount: 0,
    totalCount: 0,
    newStartsThisMonth: 0,
    endingSoonCount: 0,
    activeClientsCount: 0
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters State (initialized from sessionStorage if available)
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter || 'ACTIVE');
  const [clientFilter, setClientFilter] = useState(initialFilters.clientFilter || 'ALL');
  const [datePreset, setDatePreset] = useState(initialFilters.datePreset || 'ALL_TIME');
  const [startDate, setStartDate] = useState(initialFilters.startDate || '');
  const [endDate, setEndDate] = useState(initialFilters.endDate || '');
  const [search, setSearch] = useState(initialFilters.search || '');

  // Pagination State (initialized from sessionStorage if available)
  const [currentPage, setCurrentPage] = useState(initialFilters.currentPage || 1);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || 10);

  // UserDetails Modal State
  const [selectedEmployeeModal, setSelectedEmployeeModal] = useState(null);

  // Handle clicking on candidate name or avatar to open UserDetails modal
  const handleCandidateClick = (row) => {
    if (!row) return;
    const empId = row.EmployeeId || row.EmployeeCode;
    const companyData = {
      id: row.EntityId || 1,
      name: row.EntityName || 'Prophecy Consulting INC',
      companyName: row.EntityName || 'Prophecy Consulting INC'
    };

    const employeeData = {
      Id: row.EmployeeId,
      EmployeeId: row.EmployeeId,
      id: row.EmployeeId,
      EmployeeCode: row.EmployeeCode,
      employeeId: row.EmployeeCode,
      Name: row.CandidateName,
      FirstName: row.FirstName,
      LastName: row.LastName,
      Email: row.Email,
      Phone: row.Phone,
      Position: row.PlacedJobTitle || row.JobTitle,
      JobTitle: row.PlacedJobTitle || row.JobTitle,
      EntityId: row.EntityId || 1,
      CandidateId: row.CandidateId,
      WorkAuthorization: row.WorkAuthorization,
      VisaStatus: row.WorkAuthorization,
      EmploymentStatus: row.EmploymentStatus || 'ACTIVE',
      Status: row.EmploymentStatus || 'ACTIVE',
      EmployeeType: row.EmployeeType || 'W2'
    };

    setSelectedEmployeeModal({
      employeeId: empId,
      employee: employeeData,
      company: companyData
    });
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedEmployeeModal) {
        setSelectedEmployeeModal(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEmployeeModal]);

  // Avoid resetting currentPage to 1 on initial mount when restored from sessionStorage
  const isFirstRender = useRef(true);

  // Save filters and pagination to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
        statusFilter,
        clientFilter,
        datePreset,
        startDate,
        endDate,
        search,
        currentPage,
        pageSize
      }));
    } catch (e) {
      console.warn('Could not save to sessionStorage:', e);
    }
  }, [statusFilter, clientFilter, datePreset, startDate, endDate, search, currentPage, pageSize]);

  // Fetch report data from API
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const params = { viewMode: 'candidate' };
      if (statusFilter) params.status = statusFilter;
      if (clientFilter && clientFilter !== 'ALL') params.clientId = clientFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (search) params.search = search;

      const res = await axios.get(`${BASE_URL}/api/employees/deployments-report`, {
        headers,
        params
      });

      if (res.data && res.data.success) {
        setData(res.data.data || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
        if (res.data.clients) {
          // Deduplicate clients by normalized name
          const seen = new Set();
          const uniqueClients = [];
          for (const c of res.data.clients) {
            const norm = (c.name || '').trim().toLowerCase();
            if (norm && !seen.has(norm)) {
              seen.add(norm);
              uniqueClients.push(c);
            }
          }
          setClients(uniqueClients);
        }
      } else {
        throw new Error(res.data?.message || 'Failed to load deployments');
      }
    } catch (err) {
      console.error('Error fetching deployment report:', err);
      setError(err.response?.data?.message || err.message || 'Error loading deployment report');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, clientFilter, startDate, endDate, search]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Reset page to 1 when user modifies filters (but not on initial restore)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [statusFilter, clientFilter, startDate, endDate, search]);

  // Handle Date Preset Changes
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const now = new Date();

    if (preset === 'ALL_TIME') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'THIS_MONTH') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else if (preset === 'LAST_MONTH') {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else if (preset === 'THIS_YEAR') {
      const firstDay = new Date(now.getFullYear(), 0, 1);
      const lastDay = new Date(now.getFullYear(), 11, 31);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStatusFilter('ACTIVE');
    setClientFilter('ALL');
    setDatePreset('ALL_TIME');
    setStartDate('');
    setEndDate('');
    setSearch('');
    setCurrentPage(1);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (e) {
      console.warn('Could not clear sessionStorage:', e);
    }
  };

  const isFiltered = statusFilter !== 'ACTIVE' || clientFilter !== 'ALL' || startDate || endDate || search;

  // Pagination calculation
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!data.length) return;

    const headers = [
      'Deployment ID',
      'Candidate Name',
      'Email',
      'Phone',
      'Employee Code',
      'Client Name',
      'Placed Job Title',
      'Job Code',
      'Start Date',
      'End Date',
      'Status',
      'Bill Rate',
      'Pay Rate'
    ];

    const rows = data.map((d) => [
      d.DeploymentId || '',
      `"${(d.CandidateName || '').replace(/"/g, '""')}"`,
      d.Email || '',
      d.Phone || '',
      d.EmployeeCode || '',
      `"${(d.ClientName || '').replace(/"/g, '""')}"`,
      `"${(d.PlacedJobTitle || '').replace(/"/g, '""')}"`,
      d.JobCode || '',
      d.StartDate ? new Date(d.StartDate).toLocaleDateString('en-US') : '',
      d.EndDate ? new Date(d.EndDate).toLocaleDateString('en-US') : '',
      d.StatusCode || '',
      d.BillRate ? `$${d.BillRate}` : '',
      d.PayRate ? `$${d.PayRate}` : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Deployment_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="dr-container">
      {/* ================================================================
          Header & Actions
          ================================================================ */}
      <div className="dr-header">
        <div className="dr-header-title-area">
          <h1>
            <span className="dr-title-icon"><Briefcase size={26} /></span>
            HRM Deployment Report
          </h1>
          <p>
            Real-time tracking of active candidate deployments, project assignments, client accounts, and rolloff dates.
          </p>
        </div>

        <div className="dr-header-actions">
          <button className="dr-btn dr-btn-secondary" onClick={fetchReport} title="Refresh live data">
            <RotateCw size={15} /> Refresh
          </button>
          <button className="dr-btn dr-btn-secondary" onClick={() => window.print()} title="Print report">
            <Printer size={15} /> Print
          </button>
          <button className="dr-btn dr-btn-primary" onClick={handleExportCSV} title="Export current results to CSV">
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* ================================================================
          KPI Summary Cards
          ================================================================ */}
      <div className="dr-kpi-grid">
        {/* Active Deployments Card (Highlighted) */}
        <div className="dr-kpi-card highlight">
          <div className="dr-kpi-top">
            <span className="dr-kpi-title">Active Deployments</span>
            <div className="dr-kpi-icon-box"><Briefcase size={20} /></div>
          </div>
          <div className="dr-kpi-value-row">
            <span className="dr-kpi-value">{summary.activeCount}</span>
            <span className="dr-kpi-badge live">Live Active</span>
          </div>
          <span className="dr-kpi-subtext">Candidates actively working on client site</span>
        </div>

        {/* Total Deployments Card */}
        <div className="dr-kpi-card blue">
          <div className="dr-kpi-top">
            <span className="dr-kpi-title">Total Deployments</span>
            <div className="dr-kpi-icon-box"><Users size={20} /></div>
          </div>
          <div className="dr-kpi-value-row">
            <span className="dr-kpi-value">{summary.totalCount}</span>
          </div>
          <span className="dr-kpi-subtext">Cumulative historical placements</span>
        </div>

        {/* New Starts This Month */}
        <div className="dr-kpi-card emerald">
          <div className="dr-kpi-top">
            <span className="dr-kpi-title">New Starts (This Month)</span>
            <div className="dr-kpi-icon-box"><TrendingUp size={20} /></div>
          </div>
          <div className="dr-kpi-value-row">
            <span className="dr-kpi-value">{summary.newStartsThisMonth}</span>
          </div>
          <span className="dr-kpi-subtext">Deployments initiated in current calendar month</span>
        </div>

        {/* Ending Soon Card */}
        <div className="dr-kpi-card amber">
          <div className="dr-kpi-top">
            <span className="dr-kpi-title">Ending Soon (30 Days)</span>
            <div className="dr-kpi-icon-box"><AlertTriangle size={20} /></div>
          </div>
          <div className="dr-kpi-value-row">
            <span className="dr-kpi-value">{summary.endingSoonCount}</span>
            {summary.endingSoonCount > 0 && <span className="dr-kpi-badge" style={{ background: '#fef3c7', color: '#b45309' }}>Action Required</span>}
          </div>
          <span className="dr-kpi-subtext">Contracts expiring within the next 30 days</span>
        </div>

        {/* Active Clients */}
        <div className="dr-kpi-card purple">
          <div className="dr-kpi-top">
            <span className="dr-kpi-title">Active Clients</span>
            <div className="dr-kpi-icon-box"><Building2 size={20} /></div>
          </div>
          <div className="dr-kpi-value-row">
            <span className="dr-kpi-value">{summary.activeClientsCount}</span>
          </div>
          <span className="dr-kpi-subtext">Companies with active candidate placements</span>
        </div>
      </div>

      {/* ================================================================
          Filter & Controls Bar
          ================================================================ */}
      <div className="dr-filters-wrapper">
        {/* Status Quick-Switch Tabs */}
        <div className="dr-status-tabs">
          <div className="dr-tab-group-left">
            <button
              className={`dr-tab-btn ${statusFilter === 'ACTIVE' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ACTIVE')}
            >
              Active Deployments
              <span className="dr-tab-count">{summary.activeCount}</span>
            </button>
            <button
              className={`dr-tab-btn ${statusFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ALL')}
            >
              All Deployments
              <span className="dr-tab-count">{summary.totalCount}</span>
            </button>
            <button
              className={`dr-tab-btn ${statusFilter === 'ENDING_SOON' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ENDING_SOON')}
            >
              Ending Soon (30d)
              <span className="dr-tab-count">{summary.endingSoonCount}</span>
            </button>
            <button
              className={`dr-tab-btn ${statusFilter === 'COMPLETED' ? 'active' : ''}`}
              onClick={() => setStatusFilter('COMPLETED')}
            >
              Completed / Rolled-Off
            </button>
          </div>
        </div>

        {/* Filter Controls Row */}
        <div className="dr-filter-controls-grid">
          {/* Real-time Search */}
          <div className="dr-search-box">
            <Search size={16} className="dr-search-icon" />
            <input
              type="text"
              className="dr-search-input"
              placeholder="Search by candidate name, client, role, employee ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Client Filter Dropdown */}
          <div>
            <select
              className="dr-select"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <option value="ALL">All Clients ({clients.length})</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter & Preset */}
          <div className="dr-date-group">
            <select
              className="dr-select"
              style={{ width: '130px' }}
              value={datePreset}
              onChange={(e) => handleDatePresetChange(e.target.value)}
            >
              <option value="ALL_TIME">All Time</option>
              <option value="THIS_MONTH">This Month</option>
              <option value="LAST_MONTH">Last Month</option>
              <option value="THIS_YEAR">This Year</option>
              <option value="CUSTOM">Custom</option>
            </select>

            <input
              type="date"
              className="dr-date-input"
              placeholder="From"
              value={startDate}
              onChange={(e) => {
                setDatePreset('CUSTOM');
                setStartDate(e.target.value);
              }}
            />
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
            <input
              type="date"
              className="dr-date-input"
              placeholder="To"
              value={endDate}
              onChange={(e) => {
                setDatePreset('CUSTOM');
                setEndDate(e.target.value);
              }}
            />
          </div>

          {/* Clear Filters Button */}
          {isFiltered && (
            <div>
              <button className="dr-clear-btn" onClick={handleClearFilters}>
                <X size={14} /> Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ================================================================
          Data Table
          ================================================================ */}
      <div className="dr-table-wrapper">
        <div className="dr-table-scroll">
          {loading ? (
            <div className="dr-loading-box">
              <div className="dr-loading-spinner" />
              <p>Loading deployment records...</p>
            </div>
          ) : error ? (
            <div className="dr-empty-box">
              <AlertTriangle size={36} color="#ef4444" />
              <h3>Failed to Load Deployments</h3>
              <p>{error}</p>
              <button className="dr-btn dr-btn-primary" onClick={fetchReport}>
                Retry
              </button>
            </div>
          ) : paginatedData.length === 0 ? (
            <div className="dr-empty-box">
              <Briefcase size={42} color="#94a3b8" />
              <h3>No Deployments Found</h3>
              <p>
                {isFiltered
                  ? 'No candidates match the specified filters. Try adjusting your search or date criteria.'
                  : 'There are currently no active candidate deployments recorded in the system.'}
              </p>
              {isFiltered && (
                <button className="dr-btn dr-btn-secondary" onClick={handleClearFilters}>
                  Reset All Filters
                </button>
              )}
            </div>
          ) : (
            <table className="dr-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Employee ID</th>
                  <th>Client / Account</th>
                  <th>Job Title & Code</th>
                  <th>Contract Period</th>
                  <th>Rates</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => {
                  const statusUpper = (row.StatusCode || 'ACTIVE').toUpperCase();
                  const isEndingSoon = (() => {
                    if (!row.EndDate || statusUpper !== 'ACTIVE') return false;
                    const diff = Math.ceil((new Date(row.EndDate) - new Date()) / (1000 * 60 * 60 * 24));
                    return diff >= 0 && diff <= 30;
                  })();

                  return (
                    <tr key={row.DeploymentId}>
                      {/* Candidate Column */}
                      <td>
                        <div className="dr-candidate-cell">
                          <div
                            className="dr-avatar clickable"
                            style={{ background: getAvatarBg(row.CandidateName), cursor: 'pointer' }}
                            onClick={() => handleCandidateClick(row)}
                            title="Click to view employee details & timesheets"
                          >
                            {getInitials(row.CandidateName)}
                          </div>
                          <div className="dr-candidate-info">
                            <span 
                              className="dr-candidate-name clickable"
                              onClick={() => handleCandidateClick(row)}
                              title="Click to view employee details & timesheets"
                            >
                              {row.CandidateName}
                              {row.WorkAuthorization && (
                                <span className="dr-auth-tag">{row.WorkAuthorization}</span>
                              )}
                            </span>
                            <div className="dr-contact-sub">
                              {row.Email && <span>{row.Email}</span>}
                              {row.Email && row.Phone && <span>•</span>}
                              {row.Phone && <span>{row.Phone}</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Employee Code & Entity */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontWeight: '600', color: '#0f172a' }}>
                            {row.EmployeeCode || `EMP #${row.EmployeeId || 'N/A'}`}
                          </span>
                          {row.EntityName && (
                            <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                              {row.EntityName}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Client Column */}
                      <td>
                        <div className="dr-client-badge">
                          <Building2 size={13} color="#3b5bfd" />
                          <span>{row.ClientName || 'Direct Client'}</span>
                        </div>
                      </td>

                      {/* Job Title & Code */}
                      <td>
                        <div className="dr-job-cell">
                          <span className="dr-job-title">{row.PlacedJobTitle}</span>
                          {row.JobCode && (
                            <span className="dr-job-code">Code: {row.JobCode}</span>
                          )}
                        </div>
                      </td>

                      {/* Contract Period */}
                      <td>
                        <div className="dr-date-cell">
                          <span className="dr-date-range">
                            {formatDate(row.StartDate)} → {row.EndDate ? formatDate(row.EndDate) : 'Present'}
                          </span>
                          {calculateDurationBadge(row.StartDate, row.EndDate, statusUpper)}
                        </div>
                      </td>

                      {/* Rates */}
                      <td>
                        <div className="dr-rate-cell">
                          <span className="dr-bill-rate">
                            {formatRate(row.BillRate, row.BillRateType)} (Bill)
                          </span>
                          {row.PayRate && (
                            <span className="dr-pay-rate">
                              {formatRate(row.PayRate, row.PayRateType)} (Pay)
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        {isEndingSoon ? (
                          <span className="dr-status-badge ending-soon">
                            <AlertTriangle size={12} /> Ending Soon
                          </span>
                        ) : statusUpper === 'ACTIVE' ? (
                          <span className="dr-status-badge active">
                            <CheckCircle2 size={12} /> Active
                          </span>
                        ) : statusUpper === 'COMPLETED' ? (
                          <span className="dr-status-badge completed">
                            Completed
                          </span>
                        ) : (
                          <span className="dr-status-badge terminated">
                            {row.StatusCode}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ================================================================
            Pagination Bar
            ================================================================ */}
        {!loading && totalItems > 0 && (
          <div className="dr-pagination-bar">
            <div className="dr-pagination-info">
              Showing{' '}
              <strong>{(currentPage - 1) * pageSize + 1}</strong> to{' '}
              <strong>{Math.min(currentPage * pageSize, totalItems)}</strong> of{' '}
              <strong>{totalItems}</strong> candidate deployments
            </div>

            <div className="dr-pagination-controls">
              <select
                className="dr-select"
                style={{ width: '110px', padding: '5px 8px', fontSize: '12.5px' }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              <button
                className="dr-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                    )}
                    <button
                      className={`dr-page-btn ${p === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}

              <button
                className="dr-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* UserDetails Modal Popup */}
      {selectedEmployeeModal && (
        <div
          className="dr-modal-backdrop"
          onClick={() => setSelectedEmployeeModal(null)}
        >
          <div
            className="dr-modal-window"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dr-modal-top-bar">
              <div className="dr-modal-title-box">
                <span className="dr-modal-title">Employee Profile & Timesheets</span>
                <span className="dr-modal-subtitle">
                  {selectedEmployeeModal.employee?.Name} ({selectedEmployeeModal.employee?.EmployeeCode || selectedEmployeeModal.employeeId})
                </span>
              </div>
              <button
                type="button"
                className="dr-modal-close-btn"
                onClick={() => setSelectedEmployeeModal(null)}
                title="Close dialog (Esc)"
              >
                <X size={18} />
                <span>Close</span>
              </button>
            </div>
            <div className="dr-modal-content-body">
              <UserDetails
                employeeId={selectedEmployeeModal.employeeId}
                passedEmployee={selectedEmployeeModal.employee}
                passedCompany={selectedEmployeeModal.company}
                isModal={true}
                onClose={() => setSelectedEmployeeModal(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentReport;
